import { useEffect, useRef, useState } from 'react';
import { MascotUfo } from './MascotUfo';
import { playMascotPeekCue, playMascotPopCue } from '../timer/audio';

interface DesktopMascotProps {
  quiet?: boolean;
}

const MASCOT_TIPS = [
  '我在这儿,不催你。',
  '先放到桌面上就好。',
  '卡住也算线索。',
  '五分钟只要一个入口。',
];
const PIN_DURATION_MS = 20_000;
const TIP_VISIBLE_MS = 1_800;
const POP_TIP_VISIBLE_MS = 1_650;

export function DesktopMascot({ quiet = false }: DesktopMascotProps) {
  const [bubbleOpen, setBubbleOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [booping, setBooping] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [tipIndex, setTipIndex] = useState(0);
  const tipIndexRef = useRef(0);
  const hideTimerRef = useRef<number>(0);
  const pinTimerRef = useRef<number>(0);
  const boopTimerRef = useRef<number>(0);
  const resetTimerRef = useRef<number>(0);
  const lastPeekCueRef = useRef(0);
  const pointerInsideRef = useRef(false);
  const pinnedRef = useRef(false);

  function hideTip(delay = 0) {
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => setBubbleOpen(false), delay);
  }

  function showTip(nextIndex?: number, visibleMs = TIP_VISIBLE_MS) {
    const resolvedIndex = nextIndex ?? tipIndexRef.current;
    window.clearTimeout(hideTimerRef.current);
    tipIndexRef.current = resolvedIndex;
    setTipIndex(resolvedIndex);
    setBubbleOpen(true);
    hideTimerRef.current = window.setTimeout(() => setBubbleOpen(false), visibleMs);
  }

  function playPeekCueOnce() {
    const now = performance.now();
    if (pinned || resetting || now - lastPeekCueRef.current < 1200) return;
    lastPeekCueRef.current = now;
    playMascotPeekCue();
  }

  function pinMascot() {
    window.clearTimeout(pinTimerRef.current);
    window.clearTimeout(boopTimerRef.current);
    pinnedRef.current = true;
    setPinned(true);
    setBooping(true);
    setResetting(false);
    playMascotPopCue();
    showTip((tipIndexRef.current + 1) % MASCOT_TIPS.length, POP_TIP_VISIBLE_MS);
    boopTimerRef.current = window.setTimeout(() => setBooping(false), 620);
    pinTimerRef.current = window.setTimeout(() => {
      window.clearTimeout(resetTimerRef.current);
      window.clearTimeout(hideTimerRef.current);
      pinnedRef.current = false;
      setPinned(false);
      setBooping(false);
      setHovered(false);
      setFocused(false);
      setBubbleOpen(false);
      setResetting(true);
      if (!pointerInsideRef.current) {
        resetTimerRef.current = window.setTimeout(() => {
          setResetting(false);
          setBubbleOpen(false);
        }, 520);
      }
    }, PIN_DURATION_MS);
  }

  function revealPreview() {
    pointerInsideRef.current = true;
    if (resetting || pinnedRef.current) return;
    setHovered(true);
    playPeekCueOnce();
    showTip();
  }

  function hidePreview() {
    pointerInsideRef.current = false;
    setHovered(false);
    setResetting(false);
    if (!pinnedRef.current) {
      hideTip(220);
    }
  }

  useEffect(() => {
    window.clearTimeout(hideTimerRef.current);
    if (quiet) {
      setBubbleOpen(false);
      return;
    }

    const firstTimer = window.setTimeout(() => {
      if (!pinnedRef.current && !pointerInsideRef.current) showTip(0);
    }, 9000);
    const interval = window.setInterval(() => {
      if (!pinnedRef.current && !pointerInsideRef.current) {
        showTip((tipIndexRef.current + 1) % MASCOT_TIPS.length);
      }
    }, 48000);

    return () => {
      window.clearTimeout(firstTimer);
      window.clearInterval(interval);
      window.clearTimeout(hideTimerRef.current);
    };
  }, [quiet]);

  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);

  useEffect(() => {
    return () => {
      window.clearTimeout(hideTimerRef.current);
      window.clearTimeout(pinTimerRef.current);
      window.clearTimeout(boopTimerRef.current);
      window.clearTimeout(resetTimerRef.current);
    };
  }, []);

  const previewing = (hovered || focused) && !pinned && !resetting;
  const unitClassName = [
    'desktop-mascot-unit',
    previewing ? 'preview' : '',
    pinned ? 'pinned' : '',
    booping ? 'boop' : '',
    resetting ? 'resetting' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={`desktop-mascot${quiet ? ' quiet' : ''}`} aria-label="赛博小U">
      <div className={unitClassName}>
        <p className={`desktop-mascot-bubble${bubbleOpen ? ' visible' : ''}`} aria-live="polite">
          {MASCOT_TIPS[tipIndex]}
        </p>
        <button
          className="desktop-mascot-button"
          type="button"
          aria-label={quiet ? '赛博小U正在安静待机' : '赛博小U'}
          onPointerEnter={revealPreview}
          onPointerMove={() => {
            if (!hovered && !pinnedRef.current) revealPreview();
          }}
          onMouseEnter={revealPreview}
          onFocus={() => {
            if (resetting || pinnedRef.current) return;
            setFocused(true);
            playPeekCueOnce();
            showTip();
          }}
          onPointerLeave={hidePreview}
          onMouseLeave={hidePreview}
          onBlur={() => {
            setFocused(false);
          }}
          onClick={pinMascot}
        >
          <MascotUfo className="desktop-ufo" mood={quiet ? 'sleepy' : pinned ? 'alert' : 'calm'} />
        </button>
      </div>
    </aside>
  );
}
